import { useMemo } from 'react'

import { useDismissedMessages } from './useDismissedMessages'

import { ApolloError } from '@apollo/client'

import { useProductMessagesQuery } from '@/graphql/generated/hooks'
import { ProductMessagesQuery } from '@/graphql/generated/operations'

/**
 * Polling interval for checking new messages (5 minutes)
 */
const POLL_INTERVAL_MS = 300_000

/**
 * Return type for useProductMessages hook
 */
export interface UseProductMessagesReturnIProps {
  currentMessage: ProductMessagesQuery['productMessages'][0] | null
  isLoading: boolean
  error: ApolloError | undefined
  dismissMessage: (messageId: string) => void
}

/**
 * Hook to fetch the most recent active product message
 */
export const useProductMessages = (): UseProductMessagesReturnIProps => {
  const {
    data,
    loading: isLoading,
    error,
  } = useProductMessagesQuery({
    variables: {
      last: 1,
      filter: { isActive: true },
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: POLL_INTERVAL_MS,
  })

  const { isMessageDismissed, dismissedMessageIds, dismissMessage } =
    useDismissedMessages()
  const latestMessage = data?.productMessages?.[0] ?? null

  const currentMessage = useMemo(() => {
    if (!latestMessage) return null

    if (isMessageDismissed(latestMessage.id)) {
      return null
    }

    return latestMessage
  }, [latestMessage, isMessageDismissed, dismissedMessageIds])

  return {
    currentMessage,
    isLoading,
    error,
    dismissMessage,
  }
}
